import useAuth from '../../hooks/useAuth';
import { updatePassword } from '../../services/api';
import { wasHandledGlobally } from '../../services/client';
import {
	Button,
	Flex,
	HStack,
	Heading,
	Input,
	InputGroup,
	InputRightElement,
	PinInput,
	PinInputField,
	Stack,
	Text,
	useToast
} from '@chakra-ui/react';
import { FormEvent, useEffect, useState } from 'react';
import { AiFillEye, AiFillEyeInvisible } from 'react-icons/ai';
import { useNavigate, useParams } from 'react-router-dom';
import { CampoForm } from '../../components/CampoForm';
import { ErrosForm, useErrosForm } from '../../hooks/useErrosForm';
import { SENHA_MINIMA } from '../../utils/validacao';
import { FUNDO_AUTH } from '../../utils/fundoAuth';

type Campo = 'code' | 'password';

// O backend gera o codigo com 6 digitos (crypto.randomInt), entao a tela precisa
// dos 6 campos - com 4 nao havia como digitar o codigo inteiro.
const TAMANHO_CODIGO = 6;

export default function ChangePassword() {
	const [ code, setCode ] = useState('');
	const [ password, setPassword ] = useState('');
	const [ isLoading, setIsLoading ] = useState(false);
	const { erros, validar, limparErro } = useErrosForm<Campo>();
	const toast = useToast();
	const navigate = useNavigate();
	const [ show, setShow ] = useState(false);
	const { logout } = useAuth();
	const { email } = useParams();

	// Sem email na rota nao ha o que confirmar: volta para pedir o codigo de novo.
	useEffect(
		() => {
			if (!email) navigate('/forgot-password');
		},
		[ email, navigate ]
	);

	const montarErros = (): ErrosForm<Campo> => {
		const encontrados: ErrosForm<Campo> = {};

		if (code.length < TAMANHO_CODIGO) encontrados.code = `Digite os ${TAMANHO_CODIGO} dígitos do código.`;

		if (!password) {
			encontrados.password = 'Senha é obrigatória!';
		} else if (password.length < SENHA_MINIMA) {
			encontrados.password = `A senha precisa de pelo menos ${SENHA_MINIMA} caracteres.`;
		}

		return encontrados;
	};

	// As guardas antigas chamavam navigate sem return, entao a requisicao seguia
	// mesmo assim - e o setIsLoading(true) vinha antes da validacao, deixando o
	// botao travado em carregando quando nada era enviado.
	const handleSendToken = (event: FormEvent) => {
		event.preventDefault();
		if (!email) return navigate('/forgot-password');
		if (!validar(montarErros())) return;

		setIsLoading(true);
		updatePassword({
			codigo: parseInt(code),
			email: email + '',
			senha: password
		})
			.then(() => {
				toast({
					title: `Senha alterada com sucesso. Entre novamente.`,
					status: 'success',
					isClosable: true
				});
				logout();
			})
			.catch((error) => {
				setIsLoading(false);
				if (wasHandledGlobally(error)) return;
				toast({
					title: `Código inválido.`,
					status: 'error',
					isClosable: true
				});
			});
	};

	const handleClick = () => setShow(!show);

	return (
		<Stack
			minH={'100vh'}
			direction={{ base: 'column', md: 'row' }}
			bgImage={FUNDO_AUTH}
			bgSize={'cover'}
			bgPosition={'center'}
			bgRepeat={'no-repeat'}
		>
			<Flex p={8} flex={1} align={'center'} justify={'center'}>
				<Stack
					as={'form'}
					onSubmit={handleSendToken}
					noValidate
					spacing={4}
					w={'full'}
					maxW={'md'}
					align={'center'}
					justify={'center'}
					backgroundColor="blue.900"
					borderWidth="1px"
					borderColor="whiteAlpha.200"
					padding={'40px'}
					borderRadius="18px"
					boxShadow="0 30px 60px -30px rgba(0,0,0,0.75)"
					color="white"
				>
					<Heading fontSize={'2xl'}>Mude sua senha</Heading>

					<CampoForm label="Código enviado por e-mail" erro={erros.code} ocultarAsterisco>
						<Flex alignItems="center" flexDir="column">
							<Text textAlign="center" marginBottom="20px" marginTop="20px">
								Enviamos um código no seu email, confirme-o e digite a senha nova!
							</Text>
							<HStack>
								<PinInput
									otp
									value={code}
									onChange={(valor) => {
										setCode(valor);
										limparErro('code');
									}}
									isDisabled={isLoading}
								>
									{Array.from({ length: TAMANHO_CODIGO }).map((_, indice) => (
										<PinInputField
											key={indice}
											color="white"
											borderColor="whiteAlpha.300"
											bgColor="whiteAlpha.100"
										/>
									))}
								</PinInput>
							</HStack>
						</Flex>
					</CampoForm>

					<CampoForm label="Nova senha" htmlFor="senha" erro={erros.password} ocultarAsterisco>
						<InputGroup size="md">
							<Input
								id="senha"
								type={show ? 'text' : 'password'}
								autoComplete="new-password"
								placeholder="*********"
								value={password}
								onChange={(e) => {
									setPassword(e.target.value);
									limparErro('password');
								}}
								disabled={isLoading}
								color="white"
								borderColor="whiteAlpha.300"
								bgColor="whiteAlpha.100"
								_placeholder={{ color: 'whiteAlpha.500' }}
							/>
							<InputRightElement width="4.5rem">
								<Button
									type="button"
									h="1.75rem"
									size="sm"
									onClick={handleClick}
									aria-label={show ? 'Ocultar senha' : 'Mostrar senha'}
									variant={'unstyled'}
								>
									{show ? <AiFillEyeInvisible size={'25'} /> : <AiFillEye size={'25'} />}
								</Button>
							</InputRightElement>
						</InputGroup>
					</CampoForm>

					<Stack spacing={6} width="100%">
						<Button colorScheme={'blue'} variant={'solid'} type="submit" isLoading={isLoading}>
							Alterar senha
						</Button>
						<Button
							type="button"
							colorScheme="blue"
							variant="outline"
							onClick={() => navigate('/forgot-password')}
							isDisabled={isLoading}
						>
							Voltar
						</Button>
					</Stack>
				</Stack>
			</Flex>
		</Stack>
	);
}
